import {useCallback, useMemo, useState, type ReactNode} from "react";
import {Loader} from "@mantine/core";
import {t} from "i18next";
import {
    DocumentGenerator,
    type Company,
    type CompanySelection,
    type DocumentChange,
    type SuggestionQuery,
} from "@dibkunnskap/dokumentgenerator";
import "@dibkunnskap/dokumentgenerator/styles.css";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import RenderNodeFactory from "../RenderNodeFactory";
import {ruleset} from "../renderRules";
import {augmentations} from "../augmentations";
import documentClasses from "./document.module.css";
import contentBaseClasses from "../contentBase.module.css";
import {DocgenForm} from "./DocgenForm";
import {ResizeHandle} from "../resizable/ResizeHandle";
import {useResizable} from "../resizable/useResizable";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentGeneratorBodyProps {
    /** topicId of the document template to render. */
    documentId: string;
    /** collectionId (filling). Undefined = standalone OR checklist-draft. */
    collectionId?: string;
    /** Whether user has no saved collection (only true in checklist-draft case). */
    isDraft: boolean;
    /** Callback to open "create filling" modal. Standalone passes nothing. */
    onPromptNewFilling?: () => void;
    /**
     * Whether the user may drag the form|preview divider. The checklist's
     * document pane is a flexible grid column, so its form keeps a persisted
     * pixel width (useResizable). The standalone page owns the full content
     * box and uses a fixed CSS proportion instead (DibApp2020's 1:3), with no
     * handle and no persisted width.
     */
    resizableForm: boolean;

    // ── Slots ─────────────────────────────────────────────
    /** Above the form/preview split. Checklist puts <h1>name</h1> here; standalone leaves empty. */
    outerHeader?: ReactNode;
    /**
     * Inside the form pane, above the form. Standalone renders the reset
     * button here; a function receives `reset`, which clears the saved values
     * and conditions server-side and restarts the generator.
     */
    topOfForm?: ReactNode | ((api: {reset: () => void}) => ReactNode);
    /** Inside the preview pane, above the rendered document, spanning the full pane width (the divider under it runs to the pane's right edge, as in DibApp2020). Standalone puts <h1>name</h1> + descroot; checklist leaves empty. */
    topOfPreview?: ReactNode;
    /** Inside the form pane, below the form. Standalone puts <GenerateButtons isStandalone>; checklist leaves empty. */
    bottomOfForm?: ReactNode;
}

// ---------------------------------------------------------------------------
// Suggestion sources (the library no longer fetches; same endpoints as before)
// ---------------------------------------------------------------------------

const fetchJson = async <T,>(url: string): Promise<T> => {
    const response = await fetch(url, {
        method: "GET",
        headers: {Accept: "application/json"},
        cache: "no-cache",
        credentials: "same-origin",
    });
    if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
    }
    return (await response.json()) as T;
};

const searchSuggestions = async (query: SuggestionQuery): Promise<string[]> => {
    const params = new URLSearchParams({
        varId: query.variableId,
        varName: query.variableName,
        topicId: query.topicId,
        language: query.language,
    });
    if (query.value) params.set("varValue", query.value);
    const result = await fetchJson<string[] | {ERROR?: boolean}>(
        `/dibappapi/api/document/autosuggestions?${params.toString()}`
    );
    return Array.isArray(result) ? result : [];
};

const searchCompanies = async (query: string): Promise<Company[]> => {
    const params = new URLSearchParams({q: query});
    return fetchJson<Company[]>(`/dibapi/api/orgsearch?${params.toString()}`);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DocumentGeneratorBody = ({
    documentId,
    collectionId,
    isDraft,
    onPromptNewFilling,
    resizableForm,
    outerHeader,
    topOfForm,
    topOfPreview,
    bottomOfForm,
}: DocumentGeneratorBodyProps) => {
    const {containerRef, size, separatorProps} = useResizable({
        storageKey: "docgen:formWidthPx",
        defaultSize: 380,
        min: 300,
        max: 640,
    });

    // ── Data fetching ────────────────────────────────────────────────

    const {data: docData, isFetching} =
        dibAppApi.endpoints.getDocgenDocument.useQuery(
            {resourceId: documentId, collectionId},
            {refetchOnMountOrArgChange: true}
        );

    const {data: conditionsData, isFetching: isConditionsFetching} =
        dibAppApi.endpoints.getResourceConditions.useQuery(
            {resourceId: docData?.topicData?.id ?? ""},
            {skip: !docData?.topicData?.id}
        );

    // The generator reads the saved answers once, when it starts, so it must
    // not mount before they have arrived (nor while a reset refetches them).
    const {data: conditionUserData, isFetching: isConditionUserDataFetching} =
        dibAppApi.endpoints.getResourceConditionUserData.useQuery({
            topicId: documentId,
            collectionId,
        });
    const conditionUserDataLoaded =
        conditionUserData !== undefined && !isConditionUserDataFetching;

    // ── Mutations ────────────────────────────────────────────────────

    const [triggerPatchXVar] = dibAppApi.endpoints.patchXVariable.useMutation();
    const [triggerSetCondition] =
        dibAppApi.endpoints.setResourceCondition.useMutation();
    const [triggerAutofill] =
        dibAppApi.endpoints.autofillDocumentForOrgNr.useLazyQuery();
    const [triggerResetXVars] =
        dibAppApi.endpoints.resetXVariables.useMutation();
    const [triggerResetConditions] =
        dibAppApi.endpoints.resetConditionalUserData.useMutation();

    // Counts the completed resets. It is part of the generator key.
    const [resetCount, setResetCount] = useState(0);

    // ── Derived values ───────────────────────────────────────────────

    const documentIds = useMemo(
        () => ({topicId: documentId, collectionId}),
        [documentId, collectionId]
    );

    const showLoader =
        isFetching || isConditionsFetching || !conditionUserDataLoaded;

    const labels = useMemo(
        () => ({
            close: t("Word.Close"),
            showAll: t("Info.ShowAll"),
            alternatives: t("Label.Alternatives"),
        }),
        []
    );

    // ── Callbacks ────────────────────────────────────────────────────

    const handleChange = useCallback(
        (change: DocumentChange) => {
            if (change.type === "condition") {
                triggerSetCondition({
                    documentIds,
                    conditionLabelGroup: change.labelGroup,
                    conditionLabel: change.label,
                });
                return;
            }
            triggerPatchXVar({...documentIds, change});
        },
        [triggerPatchXVar, triggerSetCondition, documentIds]
    );

    const handlePromptNewFilling = useCallback(
        (e?: React.FocusEvent) => {
            if (!isDraft) return;
            (e?.target as HTMLElement)?.blur?.();
            onPromptNewFilling?.();
        },
        [isDraft, onPromptNewFilling]
    );

    const handleSelectCompany = useCallback(
        ({company, variableId, variableName}: CompanySelection) => {
            triggerAutofill({
                orgnr: company.orgnr,
                variableId,
                triggerName: variableName,
                documentIds,
            });
        },
        [triggerAutofill, documentIds]
    );

    const handleResetDocument = useCallback(async () => {
        try {
            await Promise.all([
                triggerResetXVars({
                    topicId: documentId,
                    collectionId,
                }).unwrap(),
                triggerResetConditions({
                    topicId: documentId,
                    collectionId,
                }).unwrap(),
            ]);
        } catch {
            return;
        }
        // The generator holds the values of the payload that started it. Start
        // a new generator, so that the form and the preview show the cleared
        // data from the refetched payload.
        setResetCount((count) => count + 1);
    }, [triggerResetXVars, triggerResetConditions, documentId, collectionId]);

    const topOfFormNode =
        typeof topOfForm === "function"
            ? topOfForm({reset: handleResetDocument})
            : topOfForm;

    // ── Render ───────────────────────────────────────────────────────

    return (
        <div
            // plainTables: the docgen ruleset skips the Table rule, so raw
            // <table> fallback HTML gets the shared base styling.
            className={`${documentClasses.documentView} ${contentBaseClasses.contentBase} ${contentBaseClasses.plainTables}`}
        >
            {outerHeader && (
                <header className={documentClasses.documentViewHeader}>
                    {outerHeader}
                </header>
            )}

            {showLoader && (
                <div
                    style={{
                        gridColumn: "1 / -1",
                        textAlign: "center",
                        padding: "2rem",
                    }}
                >
                    <Loader size="md" />
                </div>
            )}

            {docData?.topicData &&
                !isFetching &&
                !isConditionsFetching &&
                conditionUserDataLoaded && (
                <DocumentGenerator
                    key={`${docData.topicData.id}:${resetCount}`}
                    document={docData.topicData}
                    conditions={conditionsData ?? {}}
                    conditionAnswers={conditionUserData ?? []}
                    ignoreSavedValues={isDraft}
                    onChange={handleChange}
                    onSelectCompany={handleSelectCompany}
                    searchSuggestions={searchSuggestions}
                    searchCompanies={searchCompanies}
                    renderNodeFactory={RenderNodeFactory}
                    renderRules={ruleset}
                    renderAugmentations={augmentations}
                    labels={labels}
                >
                    <div
                        className={
                            resizableForm
                                ? documentClasses.documentBody
                                : `${documentClasses.documentBody} ${documentClasses.documentBodyFluid}`
                        }
                        ref={containerRef}
                    >
                        <div
                            className={documentClasses.documentForm}
                            style={
                                resizableForm
                                    ? {flexBasis: `${size}px`}
                                    : undefined
                            }
                        >
                            {topOfFormNode && (
                                <div
                                    className={documentClasses.documentFormTop}
                                >
                                    {topOfFormNode}
                                </div>
                            )}
                            <div className={documentClasses.documentFormScroll}>
                                <DocgenForm
                                    isDraft={isDraft}
                                    onPromptNewFilling={handlePromptNewFilling}
                                />
                            </div>
                            {bottomOfForm && (
                                <div
                                    className={
                                        documentClasses.documentFormBottom
                                    }
                                >
                                    {bottomOfForm}
                                </div>
                            )}
                        </div>
                        {resizableForm && <ResizeHandle {...separatorProps} />}
                        <div className={documentClasses.documentPreview}>
                            {topOfPreview}
                            <div
                                className={documentClasses.documentPreviewStage}
                            >
                                <DocumentGenerator.View />
                            </div>
                        </div>
                    </div>
                </DocumentGenerator>
            )}
        </div>
    );
};
